import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { ToastAutoHideDuration } from 'Src/consts/duration';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { useDOMEventListener } from 'Src/feature/dom-event/useDOMEventListener';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useEmailWorkflow } from 'Src/feature/provision/useEmailWorkflow';
import { useForgotPassword } from 'Src/feature/provision/useForgotPassword';
import { useProvisionAuth } from 'Src/feature/provision/useProvisionAuth';
import { useToast } from 'Src/feature/toasts/useToast';
import { useError } from 'Src/network/useError';
import { IS_LOCAL_DEV, NODE_ENV, PASSWORD, USERNAME } from 'Src/stores/appEnv';

interface LoginFormModel {
  username: string;
  password: string;
}

interface ForgetFormModel {
  email: string;
}

const { Form: LoginForm, Field: LoginFormField, Provider: LoginFormProvider } = getTypedFormComponents<LoginFormModel>();
const { Form: ForgetForm, Field: ForgetFormField } = getTypedFormComponents<ForgetFormModel>();


const sectionId = 'useProvisionLogin';

const useProvisionLogin = () => {
  const log = useLogger().getSubLogger({ name: sectionId });
  const { t } = useTranslation();
  const toast = useToast();
  const { parseError } = useError();
  const workflow = useEmailWorkflow();
  const provisionAuth  = useProvisionAuth.mutate.login();
  const passWordReset  = useForgotPassword.mutate.reset();

  // managing state w/ events should be avoided
  // - making a ref so can avoid stale state
  const eventRefHack = useRef({
    user: workflow.data.user,
    isLoading: false,
  });

  const loginForm = useForm<LoginFormModel>({
    defaultValues: { username: '', password: '' },
    schema: object().shape({
      username: string().label(t('labels.email')).required().min(5),
      password: string().label(t('labels.password')).required().min(5),
    }),
  });

  const forgetForm = useForm<ForgetFormModel>({
    defaultValues: { email: '' },
    schema: object().shape({
      email: string().label(t('labels.email')).email().required(),
    }),
  });

  const forgetFormModal = useModalControl();

  const onPasswordReset = useCallback(async () => {
    try {
      const { values, validationError } = await forgetForm.action.submit();
      if (validationError) throw validationError;
      const { email } = values;
      const response = await passWordReset.mutateAsync({ email });
      log.info('onPasswordReset()', { response });
      toast.make({
        id: `${sectionId}.onPasswordReset`,
        type: 'alert',
        autoHideDuration: ToastAutoHideDuration.Default,
        props: {
          title: t('forgotPassword.labels.resetPassword'),
          message: t('forgotPassword.labels.resetSuccess'),
          severity: 'success',
        },
      }).show();
      forgetFormModal.close();
    } catch (e) {
      const { message, messagePoints } = parseError(e);
      toast.make({
        id: `${sectionId}.onPasswordReset`,
        type: 'alert',
        autoHideDuration: ToastAutoHideDuration.Default,
        props: {
          title: t('forgotPassword.labels.resetPassword'),
          message,
          messagePoints,
          severity: 'error',
        },
      }).show();
    }
  }, [forgetForm.action, forgetFormModal, log, parseError, passWordReset, t, toast]);

  useDOMEventListener('emailWorkflow.onGoPrev', useCallback((evt: CustomEvent<null>) => {
    if (eventRefHack.current.isLoading) {
      evt.preventDefault();
    }
  }, []));

  useDOMEventListener('emailWorkflow.onGoNext', useCallback(async (evt) => {
    workflow.steps.nextLoading = true;

    if (eventRefHack.current.isLoading) {
      evt.preventDefault();
      return;
    }
    // using ref here as state will not be updated by the time this gets called
    if (!eventRefHack.current.user) {
      evt.preventDefault();
      try {
        eventRefHack.current.isLoading = true;
        workflow.steps.nextLoading = true;
        eventRefHack.current.user = undefined;
        const { values, validationError } = await loginForm.action.submit();
        if (validationError) {
          log.error('emailWorkflow.onGoNext()', validationError);
          throw validationError;
        }
        const { username, password } = values;
        const { bridgeUser } = await provisionAuth.mutateAsync({ username, password });
        log.info('emailWorkflow.onGoNext()', { bridgeUser });
        // if all good, then clear onGoNext and tell the workflow to proceed
        eventRefHack.current.isLoading = false;
        eventRefHack.current.user = bridgeUser;
        workflow.updateData({ user: bridgeUser });
        workflow.steps.goNext();
        workflow.steps.nextLoading = false;
      } catch (e) {
        workflow.steps.nextLoading = false;
        eventRefHack.current.isLoading = false;
        eventRefHack.current.user = undefined;
        const { title, message, messagePoints } = parseError(e, { activity: t('provision.errors.workflowAuth') });
        toast.make({
          id: `${sectionId}.emailWorkflow.onGoNext`,
          type: 'alert',
          autoHideDuration: ToastAutoHideDuration.Default,
          props: {
            title,
            message,
            messagePoints,
            severity: 'error',
          },
        }).show();
      }
    }
  }, [log, loginForm.action, parseError, provisionAuth, t, toast, workflow]));

  useDOMEventListener('keydown', useCallback((evt) => {
    const isEnterKey = /enter/i.test(evt.code);
    if (!isEnterKey) return;
    if (forgetFormModal.isOpen) {
      void onPasswordReset();
    }
    if (/enter/i.test(evt.code) && workflow.steps.canGoNext) {
      workflow.steps.goNext();
    }
  }, [workflow, forgetFormModal.isOpen, onPasswordReset]));

  const disableForgetFormSubmit = !forgetForm.state.canTrySubmit || passWordReset.isPending;

  useEffect(() => {
    // start fresh every time we mount
    loginForm.action.reset();
    forgetForm.action.reset();
    eventRefHack.current.isLoading = false;
    eventRefHack.current.user = undefined;
    workflow.updateData({ user: undefined });
    workflow.steps.canGoNext = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // mark when user can go next based on all fields being touched and valid
    workflow.steps.canGoNext = loginForm.state.isDirty && loginForm.state.isValid;
    // only react when login form state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginForm.state.isDirty, loginForm.state.isValid]);


  useEffect(() => {
    // FOR DX ONLY
    // - avoid having to re-enter creds during local development
    if (IS_LOCAL_DEV && NODE_ENV !== 'test') {
      setTimeout(() => {
        loginForm.action.setValue('username', USERNAME, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        loginForm.action.setValue('password', PASSWORD, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loginForm,
    forgetForm,
    forgetFormModal,
    onPasswordReset,
    disableForgetFormSubmit,
  };
};

export { ForgetForm, ForgetFormField, LoginForm, LoginFormField, LoginFormProvider, useProvisionLogin };
